import {
  AdminSpinner,
  ArrowRed,
  BestPerformingBrandsEntity,
  BestPerformingFleetEntity,
  BestPerformingLocationsEntity,
  BestPerformingPlaceholder,
  BestPerformingProviderEntity,
  BestPerformingStateEntity,
  Box,
  ClientsOverHundredEntity,
  dateFormat,
  Disclaimer,
  displayCentsInDollars,
  Div,
  FinancesWithdrawalEntity,
  formatAmount,
  H3,
  H5,
  Icon,
  Img,
  ImproveWithAIButton,
  lightTheme,
  ModalPortal,
  noImage,
  NumberType,
  P,
  PROTOCOL,
  Provider,
  renderProtocol,
  renderProviderIcon,
  renderProviderName,
  renderVDriveIcon,
  renderVDriveProviderName,
  TextWithTooltipIcon,
  VDriveProvider,
  reportError,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/** The row an "Improve with AI" click is about: the Worst lists are locations. */
export type ImproveWithAILocation = { id: number; name: string };

// Create union type for all best performing entities
type BestPerformingEntity =
  | BestPerformingBrandsEntity
  | BestPerformingLocationsEntity
  | BestPerformingStateEntity
  | BestPerformingProviderEntity
  | BestPerformingFleetEntity
  | ClientsOverHundredEntity
  | FinancesWithdrawalEntity;

async function getBestPerforming<T extends BestPerformingEntity>(
  getFunction: (params: { page: number; limit: number }) => Promise<any>,
  page: number,
  bestPerforming: T[] | null,
  setBestPerforming: Dispatch<SetStateAction<T[] | null>>,
  hasMore: boolean,
  setHasMore: Dispatch<SetStateAction<boolean>>,
  setPage: Dispatch<SetStateAction<number>>,
  setLoading: Dispatch<SetStateAction<boolean>>
) {
  if (!hasMore) return;
  setLoading(true);

  const LIMIT = 10;

  try {
    const { data } = await getFunction({
      page,
      limit: LIMIT,
    });

    if (bestPerforming === null) {
      setBestPerforming(data.data);
    } else {
      setBestPerforming((prev) => [...(prev || []), ...data.data]);
    }

    setHasMore(data.data.length === LIMIT);
    setPage(page + 1);
  } catch (error) {
    reportError(error);
  } finally {
    setLoading(false);
  }
}

export const BestPerforming = <T extends BestPerformingEntity>({
  title,
  totalAmount,
  getFunction,
  vDrive = false,
  valueColor,
  noTotalSales = false,
  customComponent,
  customDependencies,
  noLastActive,
  link,
  onImproveWithAI,
}: {
  title: string;
  totalAmount?: string;
  getFunction: (params: { page: number; limit: number }) => Promise<any>;
  vDrive?: boolean;
  valueColor?: string;
  noTotalSales?: boolean;
  customComponent?: React.ReactNode;
  customDependencies?: any;
  noLastActive?: boolean;
  link?: string;
  /**
   * Makes a Worst list's "Improve with AI" live: called with the row's
   * location. Without it the button stays the coming-soon placeholder.
   */
  onImproveWithAI?: (location: ImproveWithAILocation) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [bestPerforming, setBestPerforming] = useState<T[] | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getBestPerforming(
      getFunction,
      1,
      null,
      setBestPerforming,
      true,
      setHasMore,
      setPage,
      setLoading
    );
  }, [customDependencies]);

  useEffect(() => {
    if (inView && hasMore) {
      getBestPerforming(
        getFunction,
        page,
        bestPerforming,
        setBestPerforming,
        hasMore,
        setHasMore,
        setPage,
        setLoadingMore
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <>
      <Box $p="16px 20px" $relative $overflow="hidden">
        {loading && <AdminSpinner small absolute creme />}

        {bestPerforming?.length ? (
          <Div
            $absolute
            $right="20px"
            $top="19px"
            $rightSM="16px"
            $topSM="15px"
          >
            <Icon
              $pointer
              onClick={() => setIsOpen(true)}
              color={lightTheme.lightGray}
            >
              <ArrowRed />
            </Icon>
          </Div>
        ) : null}

        <Div $flex $between $width="100%">
          <H5 $mb={totalAmount ? '0' : '4'} $mr="16">
            {title}
          </H5>
          {customComponent && <Div $mr="32">{customComponent}</Div>}
        </Div>

        {totalAmount && (
          <H3 $mb="0" $color={valueColor}>
            {totalAmount}
          </H3>
        )}

        <Div $minHeight="350px">
          {bestPerforming?.slice(0, 5).map((item, index) => (
            <BestPerformingComponent
              key={index}
              item={item}
              index={index}
              vDrive={vDrive}
              title={title}
              valueColor={valueColor}
              noTotalSales={noTotalSales}
              noLastActive={noLastActive}
              link={link}
              onImproveWithAI={onImproveWithAI}
            />
          ))}
        </Div>

        <BestPerformingPlaceholder length={bestPerforming?.length || 0} />
      </Box>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit={title}
        titleWithTextComponent={customComponent}
      >
        {loadingMore && <AdminSpinner small absolute />}

        {bestPerforming?.map((item, index) => (
          <BestPerformingComponent
            key={index}
            item={item}
            index={index}
            vDrive={vDrive}
            title={title}
            noTotalSales={noTotalSales}
            valueColor={valueColor}
            noLastActive={noLastActive}
            link={link}
            onImproveWithAI={onImproveWithAI}
          />
        ))}
        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};

// Helper function to get the display name from any entity type
const getDisplayName = (
  item: BestPerformingEntity,
  vDrive?: boolean
): string => {
  if ('name' in item) {
    return item.name; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }
  if ('state' in item) {
    return item.state; // BestPerformingStateEntity
  }
  if ('provider' in item) {
    return vDrive
      ? renderVDriveProviderName(item.provider as VDriveProvider) || ''
      : renderProviderName(item.provider as Provider) || ''; // BestPerformingProviderEntity
  }

  return 'Unknown';
};

const getItemId = (item: BestPerformingEntity): string => {
  if ('id' in item) {
    return item.id.toString();
  }
  return '';
};

// Helper function to get the display name from any entity type
const getNumber = (item: BestPerformingEntity, vDrive?: boolean): string => {
  if ('totalOrders' in item) {
    return formatAmount(item.totalOrders, NumberType.NUMBER); // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }
  if ('amount' in item) {
    return formatAmount(item.amount as number, NumberType.USD); // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return 'Unknown';
};

// Helper function to get the display name from any entity type
const getTotalSales = (item: BestPerformingEntity): string | null => {
  if ('totalSales' in item) {
    return displayCentsInDollars(item.totalSales) || '0'; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return null;
};

// Helper function to get the display name from any entity type
const getLastActiveAt = (item: BestPerformingEntity): string | null => {
  if ('lastActiveAt' in item) {
    return item.lastActiveAt || ''; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return null;
};

// Helper function to get the display name from any entity type
const getCreatedAt = (item: BestPerformingEntity): string | null => {
  if ('createdAt' in item) {
    return item.createdAt || ''; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return null;
};

// Helper function to get the display name from any entity type
const getCompletedAt = (item: BestPerformingEntity): string | null => {
  if ('completedAt' in item) {
    return item.completedAt || ''; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return null;
};

// Helper function to get the display name from any entity type
const getProtocol = (item: BestPerformingEntity): PROTOCOL | null => {
  if ('protocol' in item) {
    return item.protocol || null; // BestPerformingBrandsEntity or BestPerformingLocationsEntity
  }

  return null;
};
// Helper function to get icon for provider entities
const getDisplayIcon = (item: BestPerformingEntity, vDrive?: boolean) => {
  if ('imageUrl' in item) {
    return item.imageUrl || noImage;
  }
  if ('provider' in item) {
    return vDrive
      ? renderVDriveIcon(item.provider as VDriveProvider)
      : renderProviderIcon(item.provider as Provider);
  }
  return null;
};

// Helper function to determine if entity should show an image
const shouldShowImage = (item: BestPerformingEntity): boolean => {
  if ('provider' in item) return true; // Always show provider icons
  return 'imageUrl' in item;
};

export const BestPerformingComponent = ({
  item,
  index,
  vDrive,
  title,
  valueColor,
  noTotalSales,
  noLastActive,
  noNumber,
  link,
  onImproveWithAI,
}: {
  item: BestPerformingEntity;
  index: number;
  vDrive?: boolean;
  title: string;
  valueColor?: string;
  noTotalSales?: boolean;
  noLastActive?: boolean;
  noNumber?: boolean;
  link?: string;
  onImproveWithAI?: (location: ImproveWithAILocation) => void;
}) => {
  const showImage = shouldShowImage(item);
  const displayIcon = getDisplayIcon(item, vDrive);

  const isWorst = title.includes('Worst');

  const color = valueColor || (isWorst ? lightTheme.primary : lightTheme.green);

  return (
    <Div
      $flex
      $alignCenter
      key={index}
      $borderBottom
      $borderColor={lightTheme.lightGray}
      $height="70px"
      $heightSM="70px"
      $gap="12px"
      $gapSM="12px"
    >
      {showImage && displayIcon && (
        <Div $flexNone>
          <Img
            alt=""
            src={displayIcon}
            $width="40px"
            $widthSM="40px"
            $height="40px"
            $heightSM="40px"
            $borderRadius="1000px"
          />
        </Div>
      )}
      <Div $flex $alignCenter $gap="20px" $width="100%" $between>
        <Div $flex $center $gap="20px">
          <Div>
            <P
              $bold
              $mb="0"
              $lineHeight="1.2"
              $pointer={!!link}
              $hoverUnderline={!!link}
              onClick={() =>
                link
                  ? window.open(`${link}/${getItemId(item)}`, '_blank')
                  : undefined
              }
            >
              {index + 1}. {getDisplayName(item, vDrive)}
            </P>
            {!isWorst && !noTotalSales && getTotalSales(item) && (
              <Disclaimer $m="0" $mt="4">
                Total Sales:{' '}
                <span
                  style={{
                    color: lightTheme.green,
                  }}
                >
                  {getTotalSales(item)}
                </span>
              </Disclaimer>
            )}
            {!noLastActive && getLastActiveAt(item) && (
              <Disclaimer $m="0" $mt="4">
                Last Active:{' '}
                {getLastActiveAt(item) &&
                  dateFormat(getLastActiveAt(item) || '')}
              </Disclaimer>
            )}

            {getProtocol(item) && (
              <Disclaimer $m="0" $mt="4">
                Speed:{' '}
                {getProtocol(item) &&
                  renderProtocol(getProtocol(item) as PROTOCOL)}
              </Disclaimer>
            )}
          </Div>
        </Div>

        <Div $flex $center $gap="4px">
          {isWorst && (
            <ImproveWithAIButton
              small
              w="170px"
              onClick={
                onImproveWithAI
                  ? () =>
                      onImproveWithAI({
                        id: Number(getItemId(item)),
                        name: getDisplayName(item),
                      })
                  : undefined
              }
            />
          )}

          {!noNumber && (
          <TextWithTooltipIcon
            bold
            color={color}
            text={getNumber(item, vDrive)}
            textColor={color}
            tooltip={
              getCreatedAt(item) || getCompletedAt(item)
                ? `
            ${
              getCreatedAt(item) &&
              `Created At: ${dateFormat(getCreatedAt(item) || '')}`
            }
            <br/>
            ${
              getCompletedAt(item) &&
              `Completed At: ${dateFormat(getCompletedAt(item) || '')}`
            }
            `
                : undefined
            }
          />
          )}
        </Div>
      </Div>
    </Div>
  );
};
